import type { ResidentDocumentGroup } from '@/features/resident/types/resident'
import type {
  ResidentApplicationData,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
} from '@/features/resident/types/resident'
import {
  deriveResidentDocumentStatusLabel,
  getResidentDocumentsForType,
} from '@/features/resident/utils/residentDocuments'
import { computeMaxMoveInDateAndReason } from '@/features/resident/utils/residentCalculationClientFallback'
import { resolveLeaseMonthFromDetail } from '@/features/resident/utils/residentPayload'
import type { ApplicationDetail, ReimbursementDetail } from '../types/domain'
import type { ReimbursementsDetailInput } from '../types/schemas'

/** Normalize API/DB dates to `YYYY-MM-DD` for DatePicker (handles ISO, MM/DD/YYYY, DD-MM-YYYY). */
export function toYmd(value: unknown): string {
  const s = String(value ?? '').trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)

  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s)
  if (mdy) {
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`
  }

  const dmy = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(s)
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`
  }

  return ''
}

/**
 * Angular/resident parity: use API `*_status` when set; otherwise infer from `document.*`
 * (`deriveResidentDocumentStatusLabel`); if still empty, default to `MISSING` (Angular UI).
 */
export function effectiveReimbursementDocumentStatus(
  documents: ResidentDocumentGroup | null | undefined,
  fileType: string,
  apiStatus: unknown,
): string {
  let current = String(apiStatus ?? '').trim()
  const derived = deriveResidentDocumentStatusLabel(getResidentDocumentsForType(documents, fileType))
  if (derived && (!current || current === 'MISSING')) {
    current = derived
  }
  if (!current) return 'MISSING'
  return current
}

export function extractFirstMonthNotPaidParts(
  residentDefault: {
    first_month_not_paid?: string
    first_month_not_paid_month?: string
    first_month_not_paid_year?: string
    rental_status_date?: string
  },
): { month: string; year: string } {
  const monthRaw = String(residentDefault.first_month_not_paid_month ?? '').trim()
  const yearRaw = String(residentDefault.first_month_not_paid_year ?? '').trim()
  if (monthRaw && yearRaw) {
    return { month: monthRaw.padStart(2, '0'), year: yearRaw }
  }

  const combined = String(residentDefault.first_month_not_paid ?? '').trim()
  if (combined) {
    const slashMatch = combined.match(/^(\d{1,2})\/(\d{4})$/)
    if (slashMatch) return { month: slashMatch[1].padStart(2, '0'), year: slashMatch[2] }

    const isoMatch = combined.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?/)
    if (isoMatch) return { month: isoMatch[2].padStart(2, '0'), year: isoMatch[1] }

    const parsed = new Date(combined)
    if (!Number.isNaN(parsed.getTime())) {
      return {
        month: String(parsed.getMonth() + 1).padStart(2, '0'),
        year: String(parsed.getFullYear()),
      }
    }
  }

  // Angular `setResidentData`: when first_month_not_paid is missing, only year is filled from
  // rental_status_date and month stays empty — so calculationBaseonDate uses Start Date (+6m),
  // NOT a fake January that pushes End Date before Start (0m 0d / $0 gross).
  const statusDate = String(residentDefault.rental_status_date ?? '').trim()
  if (statusDate) {
    const ymd = statusDate.match(/^(\d{4})-(\d{2})/)
    if (ymd) return { month: '', year: ymd[1] }
    const parsed = new Date(statusDate)
    if (!Number.isNaN(parsed.getTime())) {
      return { month: '', year: String(parsed.getFullYear()) }
    }
  }
  return { month: '', year: '' }
}

/** API returns 0/1 flags as numbers or strings; normalize to the '' | '0' | '1' the schema enum expects. */
function toZeroOneFlag(value: unknown): '' | '0' | '1' {
  const normalized = String(value ?? '').trim()
  return normalized === '0' || normalized === '1' ? normalized : ''
}

/**
 * Angular `setResidentData` + `validateData`: empty loss-adjustment amounts become `$0.00`
 * (never blank). Non-empty values are rounded like Angular `Math.round(...)`.
 */
export function hydrateLossAdjustmentAmount(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === '') {
    return '$0.00'
  }
  const cleaned = String(value).replace(/[^0-9.-]/g, '')
  const num = Number(cleaned)
  if (!Number.isFinite(num)) return '$0.00'
  return `$${Math.round(num).toFixed(2)}`
}

/** Reimbursement Core security deposit — empty when unset/zero; numeric string for {@link CurrencyInput} (no `$`). */
export function hydrateUnusedSecurityDepositAmount(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === '') {
    return ''
  }
  const cleaned = String(value).replace(/[^0-9.]/g, '')
  if (!cleaned) return ''
  const num = Number(cleaned)
  if (!Number.isFinite(num) || num <= 0) return ''
  return num.toFixed(2)
}

export function buildDetailFormValues(
  detail: ReimbursementDetail,
  application: ApplicationDetail | null,
  applicantId?: string,
): ReimbursementsDetailInput {
  const rd = detail.resident_default!
  const claimDocs = detail.document as ResidentDocumentGroup | undefined
  const firstMonthNotPaid = extractFirstMonthNotPaidParts(rd)
  const applicantUnit =
    applicantId && application?.co_resident_application?.[applicantId]?.unit_number

  const apiEndDate = toYmd(rd.adjust_max_movein_date)
  const overrideSource = String(
    (rd as Record<string, unknown>).reimbursement_end_date_override_source ?? '',
  )
  const isManualOverride = overrideSource === 'Manual Override'

  const draft: ReimbursementsDetailInput = {
    reimbursement_type: String(rd.reimbursement_type ?? ''),
    rental_status: rd.rental_status ?? '',
    rental_progress: rd.rental_progress ?? '',
    unit_number: String(applicantUnit ?? application?.unit_number ?? ''),
    reason_for_default: rd.reason_for_default ?? '',
    rental_status_date: rd.rental_status_date ?? '',
    default_review_status: rd.default_review_status ?? '',
    claim_status: rd.claim_status ?? '',
    default_paid: rd.default_paid !== undefined ? String(rd.default_paid) : '',
    default_status_date: rd.default_status_date ?? '',
    // Angular defaults covid_19 to '0'; empty fails `!covid_19` in isFormValid.
    covid_19: rd.special_note === 'COVID_19' ? '1' : toZeroOneFlag(rd.covid_19) || '0',
    cdc_declaration: toZeroOneFlag(rd.cdc_declaration),
    payment_plan: toZeroOneFlag(rd.payment_plan),
    insurance_review_ready: toZeroOneFlag(rd.insurance_review_ready),
    insurance_review_approval: toZeroOneFlag(rd.insurance_review_approval),
    liberty_authorization_ready: toZeroOneFlag(rd.liberty_authorization_ready),
    liberty_approved: toZeroOneFlag(rd.liberty_approved),
    lease_verified: toZeroOneFlag(rd.lease_verified) || '0',
    skip_claim_affidavit_required: toZeroOneFlag(rd.skip_claim_affidavit_required),
    is_final_ledger: toZeroOneFlag(rd.is_final_ledger),
    lease_agreement_status: effectiveReimbursementDocumentStatus(
      claimDocs,
      'lease_agreement',
      rd.lease_agreement_status,
    ),
    payment_ledger_status: effectiveReimbursementDocumentStatus(
      claimDocs,
      'payment_ledger',
      rd.payment_ledger_status,
    ),
    unlawful_detainer_status: effectiveReimbursementDocumentStatus(
      claimDocs,
      'eviction_lawsuit',
      rd.unlawful_detainer_status,
    ),
    skip_claim_affidavit_status: effectiveReimbursementDocumentStatus(
      claimDocs,
      'skip_claim_affidavit',
      rd.skip_claim_affidavit_status,
    ),
    resident_last_pay_date: '',
    resident_last_pay_amt: '',
    late_date: '',
    notice_of_eviction_date: '',
    ud_filed_date: '',
    unit_vacancy: '',
    date_unit_is_back_on_market: rd.date_unit_is_back_on_market ?? '',
    movein_date: detail.unit_key?.movein_date ?? rd.movein_date ?? '',
    moveout_date: rd.moveout_date ?? '',
    new_resident_lease_start_date: rd.new_resident_lease_start_date ?? '',
    first_month_not_paid_month: firstMonthNotPaid.month,
    first_month_not_paid_year: firstMonthNotPaid.year,
    timely: rd.timely ?? 'NO',
    adjusted_notice: rd.adjusted_notice ?? '',
    adjust_max_movein_date: apiEndDate,
    claim_expiration_date: rd.claim_expiration_date ?? '',
    lease_start_date: rd.lease_start_date ?? '',
    lease_end_date: rd.lease_end_date ?? '',
    unused_security_deposit: hydrateUnusedSecurityDepositAmount(rd.unused_security_deposit),
    partial_payment_amount: hydrateLossAdjustmentAmount(rd.partial_payment_amount),
    loss_adjustment: hydrateLossAdjustmentAmount(rd.loss_adjustment),
    net_amount: String(rd.net_amount ?? ''),
    notes: '',
    message: '',
    lease_month:
      resolveLeaseMonthFromDetail(detail as unknown as ResidentDetailResponse) ||
      String((rd as Record<string, unknown>).lease_month ?? (rd as Record<string, unknown>).leaseMonth ?? '').trim(),
    adjusted_reason: String(
      (rd as Record<string, unknown>).adjusted_reason ??
        (rd as Record<string, unknown>).adjustedReason ??
        '',
    ),
  }

  // Prefer persisted DB `adjust_max_movein_date`. Only fall back to the system max when
  // the API sent no end date (and there is no Manual Override already covering it).
  if (!apiEndDate && !isManualOverride) {
    const systemEnd = toYmd(
      computeMaxMoveInDateAndReason(
        draft as unknown as ResidentDefaultFormValues,
        application as unknown as ResidentApplicationData | null,
        detail as unknown as ResidentDetailResponse,
      ).max_movein_date,
    )
    if (systemEnd) draft.adjust_max_movein_date = systemEnd
  }

  return draft
}
